import { useMemo, useState } from 'react';
import { Box, Image } from '@chakra-ui/react';
import { Text, Button, Colors, Icon } from '@mercator-gcp/grout';
import { useAsyncCallback } from 'react-async-hook';
import { useToggle } from 'react-use';
import { Loading } from 'components/elements/Loading';
import SearchInput from 'components/elements/SearchInput';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import { Section } from 'components/layout/Section';
import { DeleteGroupModal } from 'components/modals/DeleteGroupModal';
import { ShareProjects } from 'components/modals/ShareProjects';
import { UpdateCollectionName } from 'components/modals/UpdateCollectionName';
import ButtonExportCollectionToCSV from 'components/widgets/Dashboard/ButtonExportCollectionToCSV';
import DashboardLeadsTable from 'components/widgets/Dashboard/DashboardLeadsTable';
import CategoryFilters from 'components/widgets/ProjectFilterToolbar/CategoryFilters';
import { removeCollection, useCollections } from 'hooks/useCollections';
import { useDebounce } from 'hooks/useDebounced';
import { useFilterCollectionProjects } from 'hooks/useFilterCollectionProjects';
import { useSelectedLeadFilters } from 'hooks/useLeadTableFiltering';
import { usePaginate } from 'hooks/usePaginate';
import { useSortedProjects } from 'hooks/useSortedProjects';
import { useSelectedCollectionQuery } from 'state/queryParams';
import { FilterViewEnum } from 'types/FilterProjects';
import { trackExportProjectList } from 'utils/analytics';
import {
  ExportProjectListGroupTypes,
  ExportProjectListSuccessStates,
  ProjectGroupTypes,
} from 'utils/analytics/constants';
import { PAGINATED_PROJECT_LIMIT } from 'utils/constants';
import { toMillion } from 'utils/currency';
import { formatInteger } from 'utils/format';

const editNameButtonStyle = { color: Colors.ForestGreen900 };

const titleContainerStyle = {
  columnGap: 'padding_8',
};

const sharedWithCounterText = {
  paddingLeft: 'padding_4',
  color: Colors.Black800,
};

const filtersStyle = {
  gap: 'padding_16',
  justifyContent: 'space-between',
};

const searchInputStyle = {
  maxWidth: '240px',
  flex: 1,
};

const CollectionDetails = () => {
  const { selectedLeadFilters: activeTabs } = useSelectedLeadFilters();
  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useSelectedCollectionQuery();
  const [keywordFilter, setKeywordFilter] = useState('');
  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const { getCollectionsList, invalidateGetCollectionsList } = useCollections();
  const { data: collectionList } = getCollectionsList;
  const { nextPage, previousPage, page: currentPage, setPageNumber } = usePaginate();
  const { sortArray } = useSortedProjects();
  const { data: collectionProjects, isLoading } = useFilterCollectionProjects({
    id: Number(selectedGroup),
    page: currentPage,
    activeTabs,
    sort: sortArray,
    keywords: debouncedKeywordFilter,
  });

  const totalCost = useMemo(() => {
    const cost = collectionProjects?.aggregations?.total_cost.value || 0;

    const estimated_cost =
      collectionProjects?.aggregations?.total_estimated_cost.value || 0;

    return cost + estimated_cost;
  }, [
    collectionProjects?.aggregations?.total_cost.value,
    collectionProjects?.aggregations?.total_estimated_cost.value,
  ]);

  const filteredProjects = useMemo(() => {
    return collectionProjects?.results || [];
  }, [collectionProjects?.results]);

  const filteredCollections = collectionList?.filter(
    ({ id }) => id === Number(selectedGroup),
  )[0];
  const [nameModal, setNameModal] = useToggle(false);
  const deleteCollection = useAsyncCallback(removeCollection);
  const handleDelete = async () => {
    await deleteCollection.execute(Number(selectedGroup));
    invalidateGetCollectionsList();
    setDeleteModalOpen(false);
    setSelectedGroup('');
  };

  const totalPages = (): number => {
    return collectionProjects?.count
      ? Math.ceil(collectionProjects.count / PAGINATED_PROJECT_LIMIT) || 1
      : 1;
  };

  return (
    <>
      <FlexColumn width="100%" height="100%" sx={{ gap: 'padding_16' }}>
        <FlexRow>
          <FlexRow.Left sx={titleContainerStyle}>
            <Text.H3>{filteredCollections?.name}</Text.H3>
            <Button
              onClick={() => setNameModal(!nameModal)}
              variant="unstyled"
              sx={editNameButtonStyle}
            >
              <Icon name={Icon.Name.Pencil} />
            </Button>
          </FlexRow.Left>
          <FlexRow.Right sx={{ gap: '8px' }}>
            <ButtonExportCollectionToCSV
              collectionId={Number(selectedGroup)}
              onMutate={(clickText) => {
                trackExportProjectList({
                  groupId: Number(selectedGroup),
                  groupType: ExportProjectListGroupTypes.COLLECTION,
                  clickText,
                  successState: ExportProjectListSuccessStates.SENT,
                });
              }}
              onSuccess={(data) => {
                trackExportProjectList({
                  groupId: Number(selectedGroup),
                  groupType: ExportProjectListGroupTypes.COLLECTION,
                  linkPath: data || undefined,
                  successState: ExportProjectListSuccessStates.SUCCESS,
                });
              }}
            />
            <Button
              variant="secondary"
              onClick={() => setIsShareOpen(true)}
              startAdornment={<Icon name={Icon.Name.AddUser} />}
            >
              Share
            </Button>
            <Tooltip
              isDisabled={
                !!filteredCollections && filteredCollections?.num_shared_with === 0
              }
              tooltipText={
                <>
                  <Text>Emails subscribed to this lead list:</Text>
                  {filteredCollections?.shared_with.map(({ id, email }) => (
                    <FlexRow
                      key={`collection-details-tooltip-row-${id}`}
                      justifyContent="left"
                    >
                      <Text key={`collection-details-tooltip-text-${id}`}>
                        {' '}
                        • {email}
                      </Text>
                    </FlexRow>
                  ))}
                </>
              }
              placement="bottom"
            >
              <Button
                variant="functional"
                startAdornment={<Icon name={Icon.Name.User} fill={Colors.Black800} />}
              >
                <Text.SmallLabel sx={sharedWithCounterText}>
                  Shared with {filteredCollections?.num_shared_with} user
                  {filteredCollections?.num_shared_with === 1 ? '' : 's'}
                </Text.SmallLabel>
              </Button>
            </Tooltip>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(true)}>
              <Icon name={Icon.Name.Trash} />
            </Button>
            {isDeleteModalOpen && (
              <DeleteGroupModal
                isLoading={deleteCollection.loading}
                isDeleteModal={isDeleteModalOpen}
                setIsDeleteModal={setDeleteModalOpen}
                onDeleteClick={() => handleDelete()}
              />
            )}
          </FlexRow.Right>
        </FlexRow>
        {!!totalCost && (
          <Box>
            <Box>
              <Text.SmallLabel>Total Pipeline Potential</Text.SmallLabel>
            </Box>
            <Box>
              <Text.Lead>{toMillion(totalCost)}</Text.Lead>
            </Box>
            <Box>
              <Text.Body color={Colors.TextQuiet}>predicted project value</Text.Body>
            </Box>
          </Box>
        )}

        <FlexRow sx={filtersStyle}>
          <CategoryFilters
            visibleTabs={[FilterViewEnum.STAR, FilterViewEnum.WATCH]}
            collectionOrGroup="collection"
          />
          <Box sx={searchInputStyle}>
            <SearchInput
              value={keywordFilter}
              onChange={setKeywordFilter}
              placeholder="Search projects by keyword"
            />
          </Box>
        </FlexRow>
        {isLoading || filteredProjects?.length > 0 ? (
          <Loading>
            <DashboardLeadsTable
              items={filteredProjects}
              leadContext={{
                groupId: selectedGroup,
                groupType: ProjectGroupTypes.COLLECTION,
              }}
              isLoading={isLoading}
              header={
                !isLoading
                  ? `${formatInteger(collectionProjects?.count)} active projects found`
                  : undefined
              }
              footer={
                <Pagination
                  currentPage={currentPage}
                  isLoading={isLoading}
                  setPage={(pageNumber) => setPageNumber(pageNumber)}
                  totalPages={totalPages()}
                  onNext={nextPage}
                  onPrev={previousPage}
                />
              }
            />
          </Loading>
        ) : (
          selectedGroup?.length === 0 && (
            <Section
              label="Save a project to view them here"
              description=""
              sx={{ alignItems: 'center' }}
            >
              <Image
                boxSize="400px"
                src="/dashboard-group-empty-state.png"
                alt="No lead lists"
              />
            </Section>
          )
        )}
      </FlexColumn>
      {isShareOpen && (
        <ShareProjects
          collectionId={Number(selectedGroup)}
          onCloseClick={() => setIsShareOpen(false)}
        />
      )}
      <UpdateCollectionName
        isOpen={nameModal}
        onClose={() => setNameModal(false)}
        filteredCollection={filteredCollections}
      />
    </>
  );
};

export default CollectionDetails;
